package fr.frux.core.designsystem.components

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.StartOffset
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.stateDescription
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.a11y.rememberReduceMotion

enum class StepSegment { DONE, CURRENT, TODO }

fun stepSegment(
    index: Int,
    currentIndex: Int,
): StepSegment =
    when {
        index < currentIndex -> StepSegment.DONE
        index == currentIndex -> StepSegment.CURRENT
        else -> StepSegment.TODO
    }

internal const val STEP_FILL_TAG = "stepFill"
private const val FILL_MILLIS = 700
private const val PULSE_MILLIS = 800
private const val PULSE_MIN_ALPHA = 0.4f

/**
 * Indicateur d'étapes de l'assistant : un segment par étape, le courant se remplit puis pulse.
 * [stateDescription] (« Étape 2 sur 3, Espèce ») est fourni par l'appelant, qui le localise.
 * Sous [reduceMotion], le segment courant est plein et opaque, sans transition.
 */
@Composable
fun FruxStepIndicator(
    labels: List<String>,
    currentIndex: Int,
    stateDescription: String,
    modifier: Modifier = Modifier,
    reduceMotion: Boolean = rememberReduceMotion(),
) {
    Row(
        modifier =
            modifier.fillMaxWidth().semantics(mergeDescendants = true) {
                this.stateDescription = stateDescription
            },
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        labels.forEachIndexed { index, label ->
            val segment = stepSegment(index, currentIndex)
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                StepBar(segment, reduceMotion)
                Text(
                    text = label,
                    style =
                        if (segment == StepSegment.CURRENT) {
                            MaterialTheme.typography.labelMedium
                        } else {
                            MaterialTheme.typography.bodySmall
                        },
                    color =
                        if (segment == StepSegment.CURRENT) {
                            MaterialTheme.colorScheme.primary
                        } else {
                            MaterialTheme.colorScheme.onSurfaceVariant
                        },
                )
            }
        }
    }
}

@Composable
private fun StepBar(
    segment: StepSegment,
    reduceMotion: Boolean,
) {
    val shape = RoundedCornerShape(50)
    val animated = segment == StepSegment.CURRENT && !reduceMotion
    val fill =
        remember(segment, reduceMotion) {
            Animatable(if (segment == StepSegment.TODO || animated) 0f else 1f)
        }
    if (animated) {
        LaunchedEffect(Unit) {
            fill.animateTo(1f, tween(FILL_MILLIS, easing = FastOutSlowInEasing))
        }
    }
    val pulse =
        if (animated) {
            rememberInfiniteTransition(label = "stepPulse")
                .animateFloat(
                    initialValue = 1f,
                    targetValue = PULSE_MIN_ALPHA,
                    animationSpec =
                        infiniteRepeatable(
                            tween(PULSE_MILLIS, easing = FastOutSlowInEasing),
                            RepeatMode.Reverse,
                            // La pulsation ne démarre qu'une fois le remplissage terminé.
                            initialStartOffset = StartOffset(FILL_MILLIS),
                        ),
                    label = "stepPulseAlpha",
                ).value
        } else {
            1f
        }
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .height(4.dp)
                .clip(shape)
                .background(MaterialTheme.colorScheme.outlineVariant),
    ) {
        Box(
            modifier =
                Modifier
                    .fillMaxHeight()
                    .fillMaxWidth(fill.value)
                    .testTag(STEP_FILL_TAG)
                    .graphicsLayer { alpha = pulse }
                    .background(MaterialTheme.colorScheme.primary, shape),
        )
    }
}
